import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col, Tabs, Tab } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";
import { getUserData } from "./const";
import Pagination from "../components/Pagination";
import { useNavigate } from "react-router-dom";
import {
  createSupplier,
  getSupplier,
  listBankAccounts,
  listSuppliers,
  updateSupplier,
  postKarigarConversion,
  generateIdempotencyKey,
  getErpRequestErrorMessage,
} from "../erpClient";
import {
  parseApiPagination,
  extractListRowsFromResponse,
} from "../utils/parseApiPagination";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  enforceMinusIfNegative,
  getMaxAllowedValidationMessage,
  getMaxAllowedAmountValidationMessage, // <-- new import
  // isWithinMaxAllowed,
  // isInputAllowed,
} from "../utils/metalAmountValidation";
import Select from "react-select";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatIndianRupee } from "../utils/formatLiveRate";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  getMobileValidationError,
} from "../utils/mobileInput";
import {
  emailInputFieldProps,
  getEmailValidationError,
} from "../utils/emailInput";
import {
  useSupplierContactExistsCheck,
  SUPPLIER_CONTACT_EXISTS_MESSAGE,
} from "../utils/useSupplierContactExistsCheck";
import MobileExistsFeedback from "../components/MobileExistsFeedback";

import { createErpSocket } from "../utils/socketIo";

const ITEMS_PER_PAGE = 10;

/** Map list/get supplier API fields to table gold / silver / balance strings */
function mapSupplierBalancesFromApi(s, fallback = {}) {
  const gold = s.gold_balance_gm ?? s.goldBalanceGm ?? s.gold ?? fallback.gold;
  const silver =
    s.silver_balance_gm ?? s.silverBalanceGm ?? s.silver ?? fallback.silver;
  const balance = s.balance ?? s.money_balance ?? fallback.balance;
  return {
    gold: gold === null || gold === undefined ? "0" : String(gold),
    silver: silver === null || silver === undefined ? "0" : String(silver),
    balance: balance === null || balance === undefined ? "0" : String(balance),
  };
}

function mapKarigarFromApi(s, fallback = {}) {
  return {
    id: s.id ?? fallback.id,
    fullName: s.name || s.fullName || fallback.fullName || "",
    companyName: s.company_name || s.companyName || fallback.companyName || "",
    shortCode: s.short_code || s.shortCode || fallback.shortCode || "",
    contactNumber:
      s.contact_number || s.contactNumber || fallback.contactNumber || "",
    email: s.email || fallback.email || "",
    address: s.address || fallback.address || "",
    city: s.city || fallback.city || "",
    gstNumber:
      s.gst_number ||
      s.GST_number ||
      s.gstNumber ||
      fallback.gstNumber ||
      "",
    panCard:
      s.pan_card || s.panCard || fallback.panCard || "",
    status: s.status ?? fallback.status ?? "",
    isPrimary:
      s.primary_supplier === 1 || s.primary_supplier === "1"
        ? 1
        : s.primary_supplier === 0 ||
            s.primary_supplier === "0" ||
            s.primary_supplier === false
          ? 0
          : fallback.isPrimary === 1
            ? 1
            : 0,
    ...mapSupplierBalancesFromApi(s, fallback),
  };
}

function mapKarigarToEditState(karigar) {
  const mapped = mapKarigarFromApi(karigar, karigar);
  return {
    ...mapped,
    residentialAddress: mapped.address || karigar.residentialAddress || "",
  };
}

function Karigar({ masterGoldRate = 0, masterSilverRate = 0 }) {
  const { buttonClass } = useButtonTableStyle();
  const navigate = useNavigate();
  const canKarigarConversion = hasPermission(PERMISSIONS.KARIGAR_CONVERSION);
  const [karigars, setKarigars] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedKarigar, setSelectedKarigar] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Add Karigar form states
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [panCard, setPanCard] = useState("");
  const [status, setStatus] = useState("active");
  // New fields
  const [gold, setGold] = useState("");
  const [silver, setSilver] = useState("");
  const [balance, setBalance] = useState("");

  const addContactExistsCheck = useSupplierContactExistsCheck(contactNumber, {
    enabled: showAddModal,
    localSuppliers: karigars,
  });

  const editContactExistsCheck = useSupplierContactExistsCheck(
    selectedKarigar?.contactNumber ?? "",
    {
      enabled: showEditModal && Boolean(selectedKarigar),
      excludeSupplierId: selectedKarigar?.id ?? null,
      localSuppliers: karigars,
    },
  );

  // Transaction modal states
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [adjustmentDate, setAdjustmentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [karigarAdjustmentGold, setKarigarAdjustmentGold] = useState("");
  const [karigarAdjustmentGoldType, setKarigarAdjustmentGoldType] =
    useState("");
  const [
    karigarAdjustmentGoldPaymentMode,
    setKarigarAdjustmentGoldPaymentMode,
  ] = useState({ value: "gold", label: "Gold" });
  const [
    karigarAdjustmentGoldBankAccount,
    setKarigarAdjustmentGoldBankAccount,
  ] = useState(null);
  const [
    karigarAdjustmentSilverBankAccount,
    setKarigarAdjustmentSilverBankAccount,
  ] = useState(null);
  const [
    karigarAdjustmentAmountBankAccount,
    setKarigarAdjustmentAmountBankAccount,
  ] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [karigarAdjustmentSilver, setKarigarAdjustmentSilver] = useState("");
  const [karigarAdjustmentSilverType, setKarigarAdjustmentSilverType] =
    useState("");
  const [karigarAdjustmentAmount, setKarigarAdjustmentAmount] = useState("");
  const [karigarAdjustmentAmountType, setKarigarAdjustmentAmountType] =
    useState("");
  const [karigarAdjustmentRemarks, setKarigarAdjustmentRemarks] = useState("");
  const [selectedAdjustmentKarigar, setSelectedAdjustmentKarigar] =
    useState(null);
  const [
    karigarAdjustmentSilverPaymentMode,
    setKarigarAdjustmentSilverPaymentMode,
  ] = useState({ value: "silver", label: "Silver" });
  const [socket, setSocket] = useState(null);

  // Conversion Modal states
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [selectedConversionKarigar, setSelectedConversionKarigar] =
    useState(null);
  const [conversionTabKey, setConversionTabKey] = useState("metalToAmount");
  const [conversionMode, setConversionMode] = useState("metalToAmount");
  const [showLiveRate, setShowLiveRate] = useState(false);
  // Conversion input states
  const [goldWeight, setGoldWeight] = useState("");
  const [goldAmount, setGoldAmount] = useState("");
  const [silverWeight, setSilverWeight] = useState("");
  const [silverAmount, setSilverAmount] = useState("");
  const userData = getUserData();

  // Utility for per gram rate
  const calculatePerGramRate = (rate) => rate / 10;
  const calculatePerGramSilverRate = (rate) => rate / 1000;

  // Effect for gold conversion (metalToAmount)
  useEffect(() => {
    if (showLiveRate && conversionMode === "metalToAmount") {
      if (goldWeight !== "" && !isNaN(Number(goldWeight))) {
        const perGramRate = calculatePerGramRate(Number(masterGoldRate));
        setGoldAmount((Number(goldWeight) * perGramRate).toFixed(2));
      } else {
        setGoldAmount("");
      }
      // Silver calculation
      if (silverWeight !== "" && !isNaN(Number(silverWeight))) {
        const perGramSilverRate = calculatePerGramSilverRate(
          Number(masterSilverRate),
        );
        setSilverAmount((Number(silverWeight) * perGramSilverRate).toFixed(2));
      } else {
        setSilverAmount("");
      }
    }
  }, [
    goldWeight,
    masterGoldRate,
    silverWeight,
    masterSilverRate,
    showLiveRate,
    conversionMode,
  ]);

  // Effect for gold conversion (amountToMetal)
  useEffect(() => {
    if (showLiveRate && conversionMode === "amountToMetal") {
      const perGramRate = calculatePerGramRate(Number(masterGoldRate));
      if (
        goldAmount !== "" &&
        !isNaN(Number(goldAmount)) &&
        perGramRate !== 0
      ) {
        setGoldWeight((Number(goldAmount) / perGramRate).toFixed(2));
      } else {
        setGoldWeight("");
      }
      // Silver calculation
      const perGramSilverRate = calculatePerGramSilverRate(
        Number(masterSilverRate),
      );
      if (
        silverAmount !== "" &&
        !isNaN(Number(silverAmount)) &&
        perGramSilverRate !== 0
      ) {
        setSilverWeight((Number(silverAmount) / perGramSilverRate).toFixed(2));
      } else {
        setSilverWeight("");
      }
    }
  }, [
    goldAmount,
    masterGoldRate,
    silverAmount,
    masterSilverRate,
    showLiveRate,
    conversionMode,
  ]);

  // Reset fields when modal opens/closes or mode changes
  useEffect(() => {
    setGoldWeight("");
    setGoldAmount("");
    setSilverWeight("");
    setSilverAmount("");
  }, [showConversionModal, conversionMode]);

  // Add state for gold/silver validation errors
  const [goldError, setGoldError] = useState("");
  const [silverError, setSilverError] = useState("");

  // Reset errors when modal opens/closes or mode changes
  useEffect(() => {
    setGoldError("");
    setSilverError("");
  }, [showConversionModal, conversionMode, selectedConversionKarigar]);

  // Add validation state for gold and silver
  const [goldValidationMsg, setGoldValidationMsg] = useState("");
  const [silverValidationMsg, setSilverValidationMsg] = useState("");

  // Add validation state for amount fields in amountToMetal mode
  const [goldAmountValidationMsg, setGoldAmountValidationMsg] = useState("");
  const [silverAmountValidationMsg, setSilverAmountValidationMsg] =
    useState("");

  const isFetching = useRef(false);

  const fetchKarigars = useCallback(async (page = 1, limit = 10, search = "") => {
    if (isFetching.current) return;
    isFetching.current = true;
    setIsLoading(true);
    try {
      const response = await listSuppliers({
        search: String(search || "").trim(),
        page,
        limit,
      });
      const rows = extractListRowsFromResponse(response, ["suppliers"]);
      if (response?.success) {
        const fetchedKarigars = rows.map((karigar) => mapKarigarFromApi(karigar));
        fetchedKarigars.sort((a, b) => b.id - a.id);
        setKarigars(fetchedKarigars);
        setTotalCount(parseApiPagination(response, fetchedKarigars.length).total);
      } else {
        setKarigars([]);
        setTotalCount(0);
        toast.error(response?.message || "Failed to fetch karigars");
      }
    } catch (error) {
      console.error("Error fetching karigar details:", error);
      toast.error(error.response?.data?.message || "Failed to fetch karigars");
      setKarigars([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, []);

  const fetchKarigarsRef = useRef(fetchKarigars);
  fetchKarigarsRef.current = fetchKarigars;

  const updateKarigar = async (updatedKarigar) => {
    try {
      const response = await updateSupplier({
        id: updatedKarigar.id,
        name: updatedKarigar.fullName,
        company_name: updatedKarigar.companyName,
        short_code: updatedKarigar.shortCode,
        contact_number: updatedKarigar.contactNumber,
        email: updatedKarigar.email,
        address: updatedKarigar.residentialAddress,
        city: updatedKarigar.city,
        gst_number: updatedKarigar.gstNumber,
        pan_card: updatedKarigar.panCard,
        status: updatedKarigar.status,
        primary_supplier: updatedKarigar.isPrimary,
        gold_balance_gm: parseFloat(updatedKarigar.gold) || 0,
        silver_balance_gm: parseFloat(updatedKarigar.silver) || 0,
        balance: parseFloat(updatedKarigar.balance) || 0,
      });
      if (response?.success) {
        toast.success("Karigar updated successfully");
        // Table rows refresh via `supplier_updated` socket only
      }
    } catch (error) {
      console.error("Error updating karigar details:", error);
      toast.error(error.response?.data?.message || "Failed to update karigar");
    }
  };

  const generateShortCode = (name) => {
    return name
      .split(" ")
      .filter((word) => word && !word.includes("-"))
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchKarigars(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchKarigars]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in Karigar, refetching karigars...",
        event.detail,
      );
      setCurrentPage(1);
      fetchKarigars(1, itemsPerPage, searchQuery);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchKarigars]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("Karigar: Connected to socket server");
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("Karigar: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("Karigar: Socket connection error:", error);
    });

    newSocket.on("supplier_updated", (data) => {
      if (!data?.supplier) return;

      setKarigars((prev) => {
        const updatedIsPrimary =
          data.supplier.primary_supplier === 1 ||
          data.supplier.primary_supplier === "1";

        return prev.map((kar) => {
          if (kar.id === data.supplier.id) {
            // Update the specific karigar
            return {
              ...mapKarigarFromApi(data.supplier, kar),
              isPrimary: updatedIsPrimary ? 1 : 0,
            };
          } else if (updatedIsPrimary && kar.isPrimary === 1) {
            // If a new karigar becomes primary, remove primary status from others
            return { ...kar, isPrimary: 0 };
          } else {
            // Keep other karigars unchanged
            return kar;
          }
        });
      });
    });

    newSocket.on("supplier_created", (data) => {
      if (!data?.supplier) return;

      const newKarigar = mapKarigarFromApi(data.supplier);

      setKarigars((prev) => {
        // Check if karigar already exists (avoid duplicates)
        const exists = prev.some((kar) => kar.id === newKarigar.id);
        if (exists) {
          return prev.map((kar) =>
            kar.id === newKarigar.id ? newKarigar : kar,
          );
        }
        // Add new karigar at the beginning (newest first)
        return [newKarigar, ...prev].sort((a, b) => b.id - a.id);
      });
    });

    newSocket.on("karigar_adjustment_created", () => {
      fetchKarigarsRef.current(currentPage, itemsPerPage, searchQuery);
    });

    newSocket.on("karigar_conversion_created", () => {
      fetchKarigarsRef.current(currentPage, itemsPerPage, searchQuery);
    });

    return () => {
      newSocket.off("supplier_updated");
      newSocket.off("supplier_created");
      newSocket.off("karigar_adjustment_created");
      newSocket.off("karigar_conversion_created");
      newSocket.disconnect();
    };
  }, [currentPage, itemsPerPage, searchQuery]);

  useEffect(() => {
    setShortCode(generateShortCode(companyName));
  }, [companyName]);

  const handleEditClick = async (karigar) => {
    setSelectedKarigar(mapKarigarToEditState(karigar));
    setShowEditModal(true);

    try {
      const response = await getSupplier({ id: karigar.id });
      const supplier = response?.data?.supplier;
      if (response?.success && supplier?.id) {
        setSelectedKarigar(mapKarigarToEditState(supplier));
      }
    } catch (error) {
      console.warn("Could not load supplier details for edit:", error);
    }
  };

  const handleSaveChanges = () => {
    // Check if the selected karigar is inactive and trying to set as primary
    if (selectedKarigar.status === "0" && selectedKarigar.isPrimary === 1) {
      toast.error("Cannot set an inactive karigar as primary.");
      return; // Exit the function early
    }

    const mobileError = getMobileValidationError(
      selectedKarigar?.contactNumber,
      { required: true },
    );
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    const emailError = getEmailValidationError(selectedKarigar?.email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (editContactExistsCheck.exists) {
      toast.error(SUPPLIER_CONTACT_EXISTS_MESSAGE);
      return;
    }

    updateKarigar(selectedKarigar);
    setShowEditModal(false);
    // Table updates via `supplier_updated` socket only (primary / row data)
  };

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Client-side status filter on the current server page
  const filteredKarigars = karigars.filter((karigar) => {
    const safeStr = (v) => (v == null ? "" : String(v));
    const isPrimaryRow =
      karigar.isPrimary === 1 ||
      karigar.isPrimary === "1" ||
      karigar.isPrimary === true;
    // Same rule as the Status column badge: Active only when status is 1 / "1"
    const isActiveRow =
      karigar.status === 1 ||
      karigar.status === "1" ||
      safeStr(karigar.status).trim() === "1";
    const matchesStatusFilter =
      statusFilter === "primary"
        ? isPrimaryRow
        : statusFilter === "1"
          ? isActiveRow
          : statusFilter === "0"
            ? !isActiveRow
            : true;

    return matchesStatusFilter;
  });

  const displayedKarigars = filteredKarigars;

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    const mobileError = getMobileValidationError(contactNumber, {
      required: true,
    });
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    const emailError = getEmailValidationError(email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (addContactExistsCheck.exists) {
      toast.error(SUPPLIER_CONTACT_EXISTS_MESSAGE);
      return;
    }

    try {
      const response = await createSupplier({
        name: fullName,
        company_name: companyName,
        short_code: shortCode,
        contact_number: contactNumber,
        email: email,
        address: address,
        city: city,
        gst_number: gstNumber,
        pan_card: panCard,
        primary_supplier: 0,
        gold_balance_gm: parseFloat(gold) || 0,
        silver_balance_gm: parseFloat(silver) || 0,
        balance: parseFloat(balance) || 0,
      });
      if (!response?.success) {
        throw new Error(response?.message || "Failed to add supplier");
      }
      toast.success("Karigar added successfully");

      // Reset form fields
      setFullName("");
      setCompanyName("");
      setShortCode("");
      setContactNumber("");
      setEmail("");
      setAddress("");
      setCity("");
      setGstNumber("");
      setPanCard("");
      setStatus("active");
      setGold(""); // reset new field
      setSilver(""); // reset new field
      setBalance(""); // reset new field
      setShowAddModal(false);

      // New row appears when `supplier_created` socket fires
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "Failed to add karigar");
    }
  };

  const handleAdjustmentClick = (karigar) => {
    setSelectedAdjustmentKarigar(karigar);
    setAdjustmentDate(new Date().toISOString().split("T")[0]);
    setKarigarAdjustmentGold("");
    setKarigarAdjustmentGoldType("");
    setKarigarAdjustmentGoldPaymentMode({ value: "gold", label: "Gold" });
    setKarigarAdjustmentGoldBankAccount(null);
    setKarigarAdjustmentSilver("");
    setKarigarAdjustmentSilverType("");
    setKarigarAdjustmentSilverBankAccount(null);
    setKarigarAdjustmentAmount("");
    setKarigarAdjustmentAmountType("");
    setKarigarAdjustmentAmountBankAccount(null);
    setKarigarAdjustmentRemarks("");
    setShowTransactionModal(true);
  };

  // Add this function below handleAdjustmentClick and above handleAdjustmentSubmit
  const handleKarigarAdjustment = async (e) => {
    e.preventDefault();

    if (!canKarigarConversion) {
      toast.error("You do not have permission for karigar conversion.");
      return;
    }

    const userId = userData?.user_id || userData?.id || "1";

    const idempotencyKey = generateIdempotencyKey();

    // Prepare request data as per API contract
    const requestData = {
      type: "KA_AD",
      id: selectedAdjustmentKarigar.id,
      idempotency_key: idempotencyKey,
      // Add by_user if available (replace with actual user ID if needed)
      // by_user: userId,
      date: adjustmentDate,
      remarks: karigarAdjustmentRemarks,
      gold_mode: karigarAdjustmentGoldPaymentMode?.value || "",
      silver_mode: karigarAdjustmentSilverPaymentMode?.value || "",
      balance_mode: karigarAdjustmentAmountPaymentMode?.value || "",
      gold_bank_type: karigarAdjustmentGoldBankAccount?.value || "",
      silver_bank_type: karigarAdjustmentSilverBankAccount?.value || "",
      balance_bank_type: karigarAdjustmentAmountBankAccount?.value || "",
      gold: parseFloat(karigarAdjustmentGold) || 0,
      forgold: karigarAdjustmentGoldType === "credit" ? 1 : 0,
      silver: parseFloat(karigarAdjustmentSilver) || 0,
      forsilver: karigarAdjustmentSilverType === "credit" ? 1 : 0,
      forbalance: karigarAdjustmentAmountType === "credit" ? 1 : 0,
      balance: parseFloat(karigarAdjustmentAmount) || 0,
    };

    // Console the request data
    // console.log("Karigar Adjustment Request Data:", requestData);

    try {
      await postKarigarConversion(requestData, { idempotencyKey });
      toast.success("Adjustment successful");
      setShowTransactionModal(false);
      fetchKarigars(currentPage, itemsPerPage, searchQuery); // Refresh the karigar list
    } catch (error) {
      console.error("Error in karigar adjustment:", error);
      toast.error(getErpRequestErrorMessage(error, "Adjustment failed"));
    }
  };

  // Add a stub for adjustment form submit handler

  // Handler for conversion icon button
  const handleConversionClick = (karigar) => {
    setSelectedConversionKarigar(karigar);
    setConversionTabKey("metalToAmount");
    setShowConversionModal(true);
  };

  // --- Add handleConversionSubmit ---
  const handleConversionSubmit = async () => {
    if (!canKarigarConversion) {
      toast.error("You do not have permission for karigar conversion.");
      return;
    }

    const userId = userData?.user_id || userData?.id || "1";

    const idempotencyKey = generateIdempotencyKey();

    const payload = {
      type: "METALCON",
      id: selectedConversionKarigar.id,
      idempotency_key: idempotencyKey,
      // by_user: userId,
      conversion_type: conversionMode === "metalToAmount" ? "MTOA" : "ATOM",
      gold: parseFloat(goldWeight) || 0,
      gold_amt: parseFloat(goldAmount) || 0,
      silver: parseFloat(silverWeight) || 0,
      silver_amt: parseFloat(silverAmount) || 0,
    };
    // console.log("karigar conversion payload:", payload);

    try {
      await postKarigarConversion(payload, { idempotencyKey });
      toast.success("Conversion successful");
      setShowConversionModal(false);
      fetchKarigars(currentPage, itemsPerPage, searchQuery);
    } catch (error) {
      console.error("Error in conversion:", error);
      toast.error(getErpRequestErrorMessage(error, "Conversion failed"));
    }
  };

  const getConversionPrediction = () => {
    if (!selectedConversionKarigar) return null;

    let predictions = [];

    if (conversionMode === "metalToAmount") {
      // Gold
      if (goldWeight && goldAmount) {
        predictions.push(
          `From Karigar Gold ${parseFloat(goldWeight).toFixed(
            2,
          )}gm will be deducted and in Balance ₹${parseFloat(
            goldAmount,
          ).toFixed(2)} will be added`,
        );
      }
      // Silver
      if (silverWeight && silverAmount) {
        predictions.push(
          `From Karigar Silver ${parseFloat(silverWeight).toFixed(
            2,
          )}gm will be deducted and in Balance ₹${parseFloat(
            silverAmount,
          ).toFixed(2)} will be added`,
        );
      }
    } else if (conversionMode === "amountToMetal") {
      // Gold
      if (goldAmount && goldWeight) {
        predictions.push(
          `From Karigar Balance ₹${parseFloat(goldAmount).toFixed(
            2,
          )} will be deducted and in Gold ${parseFloat(goldWeight).toFixed(
            2,
          )}gm will be added`,
        );
      }
      // Silver
      if (silverAmount && silverWeight) {
        predictions.push(
          `From Karigar Balance ₹${parseFloat(silverAmount).toFixed(
            2,
          )} will be deducted and in Silver ${parseFloat(silverWeight).toFixed(
            2,
          )}gm will be added`,
        );
      }
    }

    if (predictions.length === 0) return null;

    return (
      <div className="alert alert-info mb-2" style={{ fontSize: "1rem" }}>
        {predictions.map((msg, idx) => (
          <div key={idx}>{msg}</div>
        ))}
      </div>
    );
  };

  // Utility to check if any conversion input field has a value
  const isAnyConversionInputChanged = () => {
    return (
      goldWeight !== "" ||
      goldAmount !== "" ||
      silverWeight !== "" ||
      silverAmount !== ""
    );
  };

  // Fetch bank accounts for dropdown in adjustment modal
  const fetchBankDetails = async () => {
    try {
      const response = await listBankAccounts();

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankDetails = response.data.bank_accounts.map((bank) => {
          const chartAccountId =
            bank.account_id ??
            bank.accountId ??
            bank.linked_account_id ??
            bank.accounts_id ??
            bank.id;
          return {
            id: chartAccountId,
            bankName: bank.bank_name || "",
            accountHolder: bank.holder_name || "",
            accountType: bank.account_type || "",
            ifscCode: bank.ifsc_code || bank.IFSC_code || "",
            accountNumber: bank.account_number || "",
            openingBalance: bank.opening_balance || "",
            balance:
              bank.available_balance !== null &&
              bank.available_balance !== undefined
                ? bank.account_type === "metal"
                  ? `${bank.available_balance} gm`
                  : `₹${bank.available_balance}`
                : bank.account_type === "metal"
                  ? "0 gm"
                  : "₹0",
            status: bank.status === 1 ? "Active" : "Inactive",
            statusClass:
              bank.status === 1
                ? "bg-success-subtle text-success"
                : "bg-danger-subtle text-danger",
            useForInvoice: bank.invoice === 1,
            useForPOS: bank.pos === 1,
          };
        });

        fetchedBankDetails.sort((a, b) => b.id - a.id);

        const bankOptions = fetchedBankDetails.map((bank) => ({
          value: bank.id,
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
          data: bank,
        }));

        setBankAccounts(bankOptions);
      } else if (response?.success && !response.data?.bank_accounts?.length) {
        setBankAccounts([]);
        toast("No bank accounts found");
      } else if (response && response.success === false) {
        setBankAccounts([]);
        toast.error(response.message || "Failed to fetch bank details");
      } else {
        setBankAccounts([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      setBankAccounts([]);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch bank details",
      );
    }
  };

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const AMOUNT_PAYMENT_MODE_OPTIONS = [
    { value: "cash", label: "Cash" },
    { value: "upi", label: "UPI" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "card", label: "Card" },
  ];
  const [
    karigarAdjustmentAmountPaymentMode,
    setKarigarAdjustmentAmountPaymentMode,
  ] = useState(null);

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card w-100 position-relative overflow-hidden">
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Supplier/Karigar Table</h4>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-info ${buttonClass}`}
                onClick={() => navigate("/karigarledger")}
              >
                Ledger
              </button>
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => setShowAddModal(true)}
              >
                Add Supplier / Karigar
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                className="form-select w-auto ms-3"
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
                <option value="primary">Primary</option>
              </select>
            </div>

            <div className="table-responsive mb-4 border rounded-1">
              <table className="table text-nowrap mb-0 align-middle">
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>City</th>
                    <th>GST</th>
                    <th>Metal</th> {/* New column */}
                    <th>Balance</th> {/* New column */}
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedKarigars.length > 0 ? (
                    displayedKarigars.map((karigar) => (
                      <tr key={karigar.id}>
                        <td>
                          {karigar.companyName}
                          {karigar.isPrimary === 1 && (
                            <span className="badge bg-warning text-white ms-2">
                              Primary
                            </span>
                          )}
                          <br />
                          <span className="text-muted">
                            ({karigar.fullName})
                          </span>
                        </td>
                        <td>{karigar.contactNumber}</td>
                        <td>{karigar.city}</td>
                        <td>{karigar.gstNumber}</td>
                        {/* Metal column: Gold and Silver badges */}
                        <td>
                          <div>
                            <span
                              className={`badge ${
                                parseFloat(karigar.gold) > 0
                                  ? "bg-success-subtle text-success"
                                  : parseFloat(karigar.gold) < 0
                                    ? "bg-danger-subtle text-danger"
                                    : "bg-light text-dark"
                              }`}
                              style={{ fontSize: "0.85rem" }}
                            >
                              Gold: {parseFloat(karigar.gold || 0).toFixed(2)}{" "}
                              gm
                            </span>
                          </div>
                          <div className="mt-1">
                            <span
                              className={`badge ${
                                parseFloat(karigar.silver) > 0
                                  ? "bg-success-subtle text-success"
                                  : parseFloat(karigar.silver) < 0
                                    ? "bg-danger-subtle text-danger"
                                    : "bg-light text-dark"
                              }`}
                              style={{ fontSize: "0.85rem" }}
                            >
                              Silver:{" "}
                              {parseFloat(karigar.silver || 0).toFixed(2)} gm
                            </span>
                          </div>
                        </td>
                        {/* Balance column: Balance badge */}
                        <td>
                          <span
                            className={`badge ${
                              parseFloat(karigar.balance) > 0
                                ? "bg-success-subtle text-success"
                                : parseFloat(karigar.balance) < 0
                                  ? "bg-danger-subtle text-danger"
                                  : "bg-light text-dark"
                            }`}
                            style={{ fontSize: "0.85rem" }}
                          >
                            {formatIndianRupee(karigar.balance || 0)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              karigar.status === "1" || karigar.status === 1
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {karigar.status === "1" || karigar.status === 1
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`btn btn-primary btn-sm ${buttonClass}`}
                            onClick={() => handleEditClick(karigar)}
                          >
                            <Icon
                              icon="solar:pen-bold"
                              width="16"
                              height="16"
                            />
                          </button>
                          {/* New button for hand-holding-dollar */}
                          <button
                            className={`btn btn-secondary btn-sm ms-2 ${buttonClass}`}
                            onClick={() => handleAdjustmentClick(karigar)}
                            type="button"
                          >
                            <Icon
                              icon="fa6-solid:hand-holding-dollar"
                              width="16"
                              height="16"
                            />
                          </button>
                          {/* New button for currency-exchange */}
                          <button
                            className={`btn btn-info btn-sm ms-2 ${buttonClass}`}
                            type="button"
                            onClick={() => handleConversionClick(karigar)}
                          >
                            <Icon
                              icon="material-symbols:sync-alt"
                              width="16"
                              height="16"
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No karigars found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* Add Supplier/Karigar Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Supplier/Karigar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>
                  Full Name 
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>
                  Company Name 
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>
                  Short Code 
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Short Code"
                  value={shortCode}
                  onChange={(e) => setShortCode(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>
                  Contact Number 
                </Form.Label>
                <Form.Control
                  {...mobileInputFieldProps}
                  required
                  placeholder="Enter Contact Number"
                  value={contactNumber}
                  onChange={(e) =>
                    setContactNumber(sanitizeMobileInput(e.target.value))
                  }
                  isInvalid={addContactExistsCheck.exists}
                />
                <MobileExistsFeedback
                  exists={addContactExistsCheck.exists}
                  checking={addContactExistsCheck.checking}
                  message={SUPPLIER_CONTACT_EXISTS_MESSAGE}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  {...emailInputFieldProps}
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>
                  City 
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>
                  Address 
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  required
                  placeholder="Enter Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>GST Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter GST Number"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>PAN Card</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter PAN Card"
                  value={panCard}
                  onChange={(e) => setPanCard(e.target.value.toUpperCase())}
                  maxLength={10}
                />
              </Col>
            </Row>

            {/* New fields row */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Gold</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter Gold in gm"
                  value={gold}
                  onChange={(e) => setGold(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Silver</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter Silver in gm"
                  value={silver}
                  onChange={(e) => setSilver(e.target.value)}
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Balance</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter Balance"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                />
              </Col>
            </Row>

            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <Button
                type="submit"
                variant="primary"
                className={buttonClass}
                disabled={
                  addContactExistsCheck.exists || addContactExistsCheck.checking
                }
              >
                Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Supplier/Karigar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedKarigar && (
            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedKarigar.fullName}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        fullName: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedKarigar.companyName}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        companyName: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Short Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedKarigar.shortCode}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        shortCode: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Contact Number</Form.Label>
                  <Form.Control
                    {...mobileInputFieldProps}
                    value={selectedKarigar.contactNumber}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        contactNumber: sanitizeMobileInput(e.target.value),
                      })
                    }
                    isInvalid={editContactExistsCheck.exists}
                  />
                  <MobileExistsFeedback
                    exists={editContactExistsCheck.exists}
                    checking={editContactExistsCheck.checking}
                    message={SUPPLIER_CONTACT_EXISTS_MESSAGE}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    {...emailInputFieldProps}
                    value={selectedKarigar.email}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        email: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter City"
                    value={selectedKarigar.city}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        city: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter Address"
                    value={selectedKarigar.residentialAddress}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        residentialAddress: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>GST Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter GST Number"
                    value={selectedKarigar.gstNumber ?? ""}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        gstNumber: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>PAN Card</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter PAN Card"
                    value={selectedKarigar.panCard || ""}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        panCard: e.target.value.toUpperCase(),
                      })
                    }
                    maxLength={10}
                  />
                </Col>
              </Row>

              {/* New fields row */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Gold</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={selectedKarigar.gold || ""}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        gold: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Silver</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={selectedKarigar.silver || ""}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        silver: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Balance</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={selectedKarigar.balance || ""}
                    onChange={(e) =>
                      setSelectedKarigar({
                        ...selectedKarigar,
                        balance: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Karigar Status</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      label="Active"
                      name="status"
                      value="1"
                      checked={
                        selectedKarigar.status === "1" ||
                        selectedKarigar.status === 1
                      }
                      onChange={(e) =>
                        setSelectedKarigar({
                          ...selectedKarigar,
                          status: e.target.value,
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="Inactive"
                      name="status"
                      value="0"
                      checked={
                        !(
                          selectedKarigar.status === "1" ||
                          selectedKarigar.status === 1
                        )
                      }
                      onChange={(e) =>
                        setSelectedKarigar({
                          ...selectedKarigar,
                          status: e.target.value,
                        })
                      }
                    />
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Primary Karigar</Form.Label>
                  <Form.Check
                    type="checkbox"
                    label="Set as Primary Karigar"
                    checked={selectedKarigar.isPrimary === 1}
                    onChange={(e) => {
                      setSelectedKarigar({
                        ...selectedKarigar,
                        isPrimary: e.target.checked ? 1 : 0,
                      });
                    }}
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSaveChanges}
            disabled={
              editContactExistsCheck.exists || editContactExistsCheck.checking
            }
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Transaction Modal */}
      <Modal
        show={showTransactionModal}
        onHide={() => setShowTransactionModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Account Settlement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleKarigarAdjustment}>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={adjustmentDate}
                  onChange={(e) => setAdjustmentDate(e.target.value)}
                />
              </Col>
            </Row>
            {/* Gold Transaction Row with Payment Mode and Bank Account */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Gold Transaction</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter gold in gm"
                  value={karigarAdjustmentGold}
                  onChange={(e) => setKarigarAdjustmentGold(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>For Gold</Form.Label>
                <div className="d-flex gap-3 mb-2">
                  <Form.Check
                    type="radio"
                    label="Aavak"
                    name="karigarAdjustmentGoldType"
                    value="credit"
                    checked={karigarAdjustmentGoldType === "credit"}
                    onChange={() => setKarigarAdjustmentGoldType("credit")}
                  />
                  <Form.Check
                    type="radio"
                    label="Jaavak"
                    name="karigarAdjustmentGoldType"
                    value="debit"
                    checked={karigarAdjustmentGoldType === "debit"}
                    onChange={() => setKarigarAdjustmentGoldType("debit")}
                  />
                </div>
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">Payment Mode</Form.Label>
                <Select
                  options={[{ value: "gold", label: "Gold" }]}
                  value={karigarAdjustmentGoldPaymentMode}
                  onChange={setKarigarAdjustmentGoldPaymentMode}
                  isClearable={false}
                  isSearchable={false}
                  placeholder="Select Payment Mode"
                />
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">
                  Received In / Paid From
                </Form.Label>
                <Select
                  options={bankAccounts}
                  value={karigarAdjustmentGoldBankAccount}
                  onChange={setKarigarAdjustmentGoldBankAccount}
                  isLoading={loadingBankAccounts}
                  placeholder="Select Bank"
                  isClearable
                />
              </Col>
            </Row>
            {/* Silver Transaction Row with Payment Mode */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Silver Transaction</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter silver in gm"
                  value={karigarAdjustmentSilver}
                  onChange={(e) => setKarigarAdjustmentSilver(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>For Silver</Form.Label>
                <div className="d-flex gap-3 mb-2">
                  <Form.Check
                    type="radio"
                    label="Aavak"
                    name="karigarAdjustmentSilverType"
                    value="credit"
                    checked={karigarAdjustmentSilverType === "credit"}
                    onChange={() => setKarigarAdjustmentSilverType("credit")}
                  />
                  <Form.Check
                    type="radio"
                    label="Jaavak"
                    name="karigarAdjustmentSilverType"
                    value="debit"
                    checked={karigarAdjustmentSilverType === "debit"}
                    onChange={() => setKarigarAdjustmentSilverType("debit")}
                  />
                </div>
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">Payment Mode</Form.Label>
                <Select
                  options={[{ value: "silver", label: "Silver" }]}
                  value={karigarAdjustmentSilverPaymentMode}
                  onChange={setKarigarAdjustmentSilverPaymentMode}
                  isClearable={false}
                  isSearchable={false}
                  placeholder="Select Payment Mode"
                />
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">
                  Received In / Paid From
                </Form.Label>
                <Select
                  options={bankAccounts}
                  value={karigarAdjustmentSilverBankAccount}
                  onChange={setKarigarAdjustmentSilverBankAccount}
                  isLoading={loadingBankAccounts}
                  placeholder="Select Bank"
                  isClearable
                />
              </Col>
            </Row>
            {/* Silver and Amount Transaction Rows (unchanged) */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Amount Transaction</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter amount"
                  value={karigarAdjustmentAmount}
                  onChange={(e) => setKarigarAdjustmentAmount(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>For Amount</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Aavak"
                    name="karigarAdjustmentAmountType"
                    value="credit"
                    checked={karigarAdjustmentAmountType === "credit"}
                    onChange={() => setKarigarAdjustmentAmountType("credit")}
                  />
                  <Form.Check
                    type="radio"
                    label="Jaavak"
                    name="karigarAdjustmentAmountType"
                    value="debit"
                    checked={karigarAdjustmentAmountType === "debit"}
                    onChange={() => setKarigarAdjustmentAmountType("debit")}
                  />
                </div>
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">Payment Mode</Form.Label>
                <Select
                  options={AMOUNT_PAYMENT_MODE_OPTIONS}
                  value={karigarAdjustmentAmountPaymentMode}
                  onChange={setKarigarAdjustmentAmountPaymentMode}
                  isClearable
                  isSearchable
                  placeholder="Select Mode"
                />
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">
                  Received In / Paid From
                </Form.Label>
                <Select
                  options={bankAccounts}
                  value={karigarAdjustmentAmountBankAccount}
                  onChange={setKarigarAdjustmentAmountBankAccount}
                  isLoading={loadingBankAccounts}
                  placeholder="Select Bank"
                  isClearable
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Enter remarks"
                  value={karigarAdjustmentRemarks}
                  onChange={(e) => setKarigarAdjustmentRemarks(e.target.value)}
                />
              </Col>
            </Row>
            <div className="d-flex justify-content-end">
              <Button
                type="submit"
                variant="primary"
                disabled={!canKarigarConversion}
                title={
                  !canKarigarConversion
                    ? "No permission for karigar conversion"
                    : undefined
                }
              >
                Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
        {/* Remove the Modal.Footer Submit button for Transaction Modal */}
        {/* <Modal.Footer>
          <Button type="submit" variant="primary">
            Submit
          </Button>
        </Modal.Footer> */}
      </Modal>

      {/* Conversion Modal */}
      <Modal
        show={showConversionModal}
        onHide={() => setShowConversionModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <div className="d-flex w-100 flex-row align-items-center justify-content-between gap-2">
            <Modal.Title className="mb-0">Metal-Amount Conversion</Modal.Title>
            {selectedConversionKarigar && (
              <div className="d-flex flex-row flex-wrap gap-2 align-items-center justify-content-end w-auto">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar.gold) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar.gold) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{ fontSize: "0.85rem" }}
                >
                  Gold:{" "}
                  {parseFloat(selectedConversionKarigar.gold || 0).toFixed(2)}{" "}
                  gm
                </span>
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar.silver) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar.silver) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{ fontSize: "0.85rem" }}
                >
                  Silver:{" "}
                  {parseFloat(selectedConversionKarigar.silver || 0).toFixed(2)}{" "}
                  gm
                </span>
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar.balance) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar.balance) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{ fontSize: "0.85rem" }}
                >
                  Balance: ₹
                  {parseFloat(selectedConversionKarigar.balance || 0).toFixed(
                    2,
                  )}
                </span>
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              {/* Show live rates if checked */}
              {/* {showLiveRate && (
                <div className="mb-3 d-flex gap-4 justify-content-center align-items-center">
                  <div className="bg-light rounded px-3 py-2 border">
                    <span className="text-muted">Master Gold Rate:</span>
                    <span className="ms-2 fw-bold text-warning">
                      ₹
                      {masterGoldRate?.toFixed
                        ? masterGoldRate.toFixed(2)
                        : masterGoldRate}
                    </span>
                  </div>
                  <div className="bg-light rounded px-3 py-2 border">
                    <span className="text-muted">Master Silver Rate:</span>
                    <span className="ms-2 fw-bold text-secondary">
                      ₹
                      {masterSilverRate?.toFixed
                        ? masterSilverRate.toFixed(2)
                        : masterSilverRate}
                    </span>
                  </div>
                </div>
              )} */}
              {/* Swap icon above both rows on md+, between rows on small screens */}
              <div className="d-none d-md-flex justify-content-center mb-2">
                <Button
                  variant="light"
                  size="sm"
                  className="rounded-circle border"
                  style={{ padding: 0, width: 32, height: 32 }}
                  onClick={() =>
                    setConversionMode(
                      conversionMode === "metalToAmount"
                        ? "amountToMetal"
                        : "metalToAmount",
                    )
                  }
                  aria-label="Swap conversion direction"
                >
                  <Icon
                    icon="material-symbols:swap-horiz"
                    width="20"
                    height="20"
                  />
                </Button>
              </div>
              {/* Gold/Amount Row */}
              <div className="d-flex flex-column flex-md-row align-items-center mb-3">
                <div className="w-100 order-1 order-md-1">
                  <Form.Label className="mb-1">
                    {conversionMode === "metalToAmount"
                      ? "Gold (gm)"
                      : "Amount (₹)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      conversionMode === "metalToAmount"
                        ? "Enter gold in gm"
                        : "Enter gold amount"
                    }
                    value={
                      conversionMode === "metalToAmount"
                        ? enforceMinusIfNegative(
                            selectedConversionKarigar?.gold,
                            goldWeight,
                          )
                        : enforceMinusIfNegative(
                            selectedConversionKarigar?.balance,
                            goldAmount,
                          )
                    }
                    onChange={(e) => {
                      if (conversionMode === "metalToAmount") {
                        const val = enforceMinusIfNegative(
                          selectedConversionKarigar?.gold,
                          e.target.value,
                        );
                        const validationMsg = getMaxAllowedValidationMessage(
                          selectedConversionKarigar?.gold,
                          val,
                          "Gold",
                        );
                        setGoldValidationMsg(validationMsg);
                        if (!validationMsg) {
                          setGoldWeight(val);
                        }
                        // If invalid, do not update the state
                      } else {
                        const val = enforceMinusIfNegative(
                          selectedConversionKarigar?.balance,
                          e.target.value,
                        );
                        // Use the new value for gold, and the current value for silver
                        const validationMsg =
                          getMaxAllowedAmountValidationMessage(
                            selectedConversionKarigar?.balance,
                            val,
                            silverAmount,
                          );
                        setGoldAmountValidationMsg(validationMsg);
                        setSilverAmountValidationMsg(validationMsg); // show on both fields
                        if (!validationMsg) {
                          setGoldAmount(val);
                        }
                        // If invalid, do not update the state
                      }
                    }}
                    disabled={
                      showLiveRate && conversionMode === "amountToMetal"
                        ? false
                        : showLiveRate && conversionMode === "metalToAmount"
                          ? false
                          : false
                    }
                  />
                  {conversionMode === "metalToAmount" && goldValidationMsg && (
                    <div className="text-danger" style={{ fontSize: "0.9em" }}>
                      {goldValidationMsg}
                    </div>
                  )}
                  {conversionMode === "amountToMetal" &&
                    goldAmountValidationMsg && (
                      <div
                        className="text-danger"
                        style={{ fontSize: "0.9em" }}
                      >
                        {goldAmountValidationMsg}
                      </div>
                    )}
                </div>
                <div
                  className="w-100 d-flex justify-content-center order-2 order-md-2 my-2 my-md-0"
                  style={{ minWidth: 0 }}
                >
                  <span className="fw-bold">converts to</span>
                </div>
                <div className="w-100 order-3 order-md-3">
                  <Form.Label className="mb-1">
                    {conversionMode === "metalToAmount"
                      ? "Amount (₹)"
                      : "Gold (gm)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      conversionMode === "metalToAmount"
                        ? "Gold Amount"
                        : "Gold in gm"
                    }
                    value={
                      conversionMode === "metalToAmount"
                        ? enforceMinusIfNegative(
                            selectedConversionKarigar?.gold,
                            goldAmount,
                          )
                        : enforceMinusIfNegative(
                            selectedConversionKarigar?.balance,
                            goldWeight,
                          )
                    }
                    onChange={(e) => {
                      if (conversionMode === "metalToAmount") {
                        setGoldAmount(
                          enforceMinusIfNegative(
                            selectedConversionKarigar?.gold,
                            e.target.value,
                          ),
                        );
                      } else {
                        setGoldWeight(
                          enforceMinusIfNegative(
                            selectedConversionKarigar?.balance,
                            e.target.value,
                          ),
                        );
                      }
                    }}
                    disabled={showLiveRate ? true : false}
                  />
                </div>
              </div>
              {/* Swap icon between rows on small screens only (vertical arrows) */}
              <div className="d-flex d-md-none justify-content-center mb-2">
                <Button
                  variant="light"
                  size="sm"
                  className="rounded-circle border"
                  style={{ padding: 0, width: 32, height: 32 }}
                  onClick={() =>
                    setConversionMode(
                      conversionMode === "metalToAmount"
                        ? "amountToMetal"
                        : "metalToAmount",
                    )
                  }
                  aria-label="Swap conversion direction"
                >
                  <Icon
                    icon="material-symbols:swap-vert"
                    width="20"
                    height="20"
                  />
                </Button>
              </div>
              {/* Silver/Amount Row (now with live rate logic) */}
              <div className="d-flex flex-column flex-md-row align-items-center mb-3">
                <div className="w-100 order-1 order-md-1">
                  <Form.Label className="mb-1">
                    {conversionMode === "metalToAmount"
                      ? "Silver (gm)"
                      : "Amount (₹)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      conversionMode === "metalToAmount"
                        ? "Enter silver in gm"
                        : "Enter silver amount"
                    }
                    value={
                      conversionMode === "metalToAmount"
                        ? enforceMinusIfNegative(
                            selectedConversionKarigar?.silver,
                            silverWeight,
                          )
                        : enforceMinusIfNegative(
                            selectedConversionKarigar?.balance,
                            silverAmount,
                          )
                    }
                    onChange={(e) => {
                      if (conversionMode === "metalToAmount") {
                        const val = enforceMinusIfNegative(
                          selectedConversionKarigar?.silver,
                          e.target.value,
                        );
                        const validationMsg = getMaxAllowedValidationMessage(
                          selectedConversionKarigar?.silver,
                          val,
                          "Silver",
                        );
                        setSilverValidationMsg(validationMsg);
                        if (!validationMsg) {
                          setSilverWeight(val);
                        }
                        // If invalid, do not update the state
                      } else {
                        const val = enforceMinusIfNegative(
                          selectedConversionKarigar?.balance,
                          e.target.value,
                        );
                        // Use the new value for silver, and the current value for gold
                        const validationMsg =
                          getMaxAllowedAmountValidationMessage(
                            selectedConversionKarigar?.balance,
                            goldAmount,
                            val,
                          );
                        setGoldAmountValidationMsg(validationMsg);
                        setSilverAmountValidationMsg(validationMsg); // show on both fields
                        if (!validationMsg) {
                          setSilverAmount(val);
                        }
                        // If invalid, do not update the state
                      }
                    }}
                    disabled={
                      showLiveRate && conversionMode === "amountToMetal"
                        ? false
                        : showLiveRate && conversionMode === "metalToAmount"
                          ? false
                          : false
                    }
                  />
                  {conversionMode === "metalToAmount" &&
                    silverValidationMsg && (
                      <div
                        className="text-danger"
                        style={{ fontSize: "0.9em" }}
                      >
                        {silverValidationMsg}
                      </div>
                    )}
                  {conversionMode === "amountToMetal" &&
                    silverAmountValidationMsg && (
                      <div
                        className="text-danger"
                        style={{ fontSize: "0.9em" }}
                      >
                        {silverAmountValidationMsg}
                      </div>
                    )}
                </div>
                <div
                  className="w-100 d-flex justify-content-center order-2 order-md-2 my-2 my-md-0"
                  style={{ minWidth: 0 }}
                >
                  <span className="fw-bold">converts to</span>
                </div>
                <div className="w-100 order-3 order-md-3">
                  <Form.Label className="mb-1">
                    {conversionMode === "metalToAmount"
                      ? "Amount (₹)"
                      : "Silver (gm)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      conversionMode === "metalToAmount"
                        ? "Silver Amount"
                        : "Silver in gm"
                    }
                    value={
                      conversionMode === "metalToAmount"
                        ? enforceMinusIfNegative(
                            selectedConversionKarigar?.silver,
                            silverAmount,
                          )
                        : enforceMinusIfNegative(
                            selectedConversionKarigar?.balance,
                            silverWeight,
                          )
                    }
                    onChange={(e) => {
                      if (conversionMode === "metalToAmount") {
                        setSilverAmount(
                          enforceMinusIfNegative(
                            selectedConversionKarigar?.silver,
                            e.target.value,
                          ),
                        );
                      } else {
                        setSilverWeight(
                          enforceMinusIfNegative(
                            selectedConversionKarigar?.balance,
                            e.target.value,
                          ),
                        );
                      }
                    }}
                    disabled={showLiveRate ? true : false}
                  />
                </div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {/* Prediction/Badge Row for Metal to Amount */}
          {conversionMode === "metalToAmount" && (
            <div className="w-100 mb-3">
              {/* Gold Row */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar?.gold) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar?.gold) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Gold:{" "}
                  {parseFloat(selectedConversionKarigar?.gold || 0).toFixed(2)}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    const result = prev - upd;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Gold:{" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    return (prev - upd).toFixed(2);
                  })()}
                </span>
                {/* <span
                  className="ms-2 text-muted text-break mt-2 mt-md-0"
                  style={{ fontSize: "0.95rem" }}
                >
                  {selectedConversionKarigar?.gold} - ({goldWeight || "0"}) ={" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    return (prev - upd).toFixed(2);
                  })()}
                </span> */}
              </div>
              {/* Silver Row */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar?.silver) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar?.silver) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Silver:{" "}
                  {parseFloat(selectedConversionKarigar?.silver || 0).toFixed(
                    2,
                  )}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    const result = prev - upd;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Silver:{" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    return (prev - upd).toFixed(2);
                  })()}
                </span>
                {/* <span
                  className="ms-2 text-muted text-break mt-2 mt-md-0"
                  style={{ fontSize: "0.95rem" }}
                >
                  {selectedConversionKarigar?.silver} - ({silverWeight || "0"})
                  ={" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    return (prev - upd).toFixed(2);
                  })()}
                </span> */}
              </div>
              {/* Balance Row (with calculation for both goldAmount and silverAmount) */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar?.balance) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar?.balance) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 110,
                    justifyContent: "center",
                  }}
                >
                  Balance: ₹
                  {parseFloat(selectedConversionKarigar?.balance || 0).toFixed(
                    2,
                  )}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    const result = prev + updGold + updSilver;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 110,
                    justifyContent: "center",
                  }}
                >
                  Balance: ₹
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    return (prev + updGold + updSilver).toFixed(2);
                  })()}
                </span>
                {/* <span
                  className="ms-2 text-muted text-break mt-2 mt-md-0"
                  style={{ fontSize: "0.95rem" }}
                >
                  {selectedConversionKarigar?.balance} + ({goldAmount || "0"}) +
                  ({silverAmount || "0"}) ={" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    return (prev + updGold + updSilver).toFixed(2);
                  })()}
                </span> */}
              </div>
            </div>
          )}
          {/* End prediction row */}
          {/* Prediction/Badge Row for Amount to Metal */}
          {conversionMode === "amountToMetal" && (
            <div className="w-100 mb-3">
              {/* Gold Row (with calculation for amount to metal) */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar?.gold) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar?.gold) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Gold:{" "}
                  {parseFloat(selectedConversionKarigar?.gold || 0).toFixed(2)}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    const result = prev + upd;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Gold:{" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    return (prev + upd).toFixed(2);
                  })()}
                </span>
                {/* <span
                  className="ms-2 text-muted text-break mt-2 mt-md-0"
                  style={{ fontSize: "0.95rem" }}
                >
                  {selectedConversionKarigar?.gold} + ({goldWeight || "0"}) ={" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    return (prev + upd).toFixed(2);
                  })()}
                </span> */}
              </div>
              {/* Silver Row (with calculation for amount to metal) */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar?.silver) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar?.silver) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Silver:{" "}
                  {parseFloat(selectedConversionKarigar?.silver || 0).toFixed(
                    2,
                  )}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    const result = prev + upd;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Silver:{" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    return (prev + upd).toFixed(2);
                  })()}
                </span>
                {/* <span
                  className="ms-2 text-muted text-break mt-2 mt-md-0"
                  style={{ fontSize: "0.95rem" }}
                >
                  {selectedConversionKarigar?.silver} + ({silverWeight || "0"})
                  ={" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    return (prev + upd).toFixed(2);
                  })()}
                </span> */}
              </div>
              {/* Balance Row (with calculation for amount to metal, both goldAmount and silverAmount) */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionKarigar?.balance) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionKarigar?.balance) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 110,
                    justifyContent: "center",
                  }}
                >
                  Balance: ₹
                  {parseFloat(selectedConversionKarigar?.balance || 0).toFixed(
                    2,
                  )}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    const result = prev - updGold - updSilver;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 110,
                    justifyContent: "center",
                  }}
                >
                  Balance: ₹
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    return (prev - updGold - updSilver).toFixed(2);
                  })()}
                </span>
                {/* <span
                  className="ms-2 text-muted text-break mt-2 mt-md-0"
                  style={{ fontSize: "0.95rem" }}
                >
                  {selectedConversionKarigar?.balance} - ({goldAmount || "0"}) -
                  ({silverAmount || "0"}) ={" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionKarigar?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    return (prev - updGold - updSilver).toFixed(2);
                  })()}
                </span> */}
              </div>
            </div>
          )}
          {/* End prediction row */}
          <div className="d-flex w-100 align-items-center justify-content-between">
            <Form.Check
              type="checkbox"
              id="liveRateCheckbox"
              label="Live Rate"
              className="mb-0"
              checked={showLiveRate}
              onChange={(e) => setShowLiveRate(e.target.checked)}
            />
            <Button
              variant="primary"
              onClick={handleConversionSubmit}
              disabled={!canKarigarConversion}
              title={
                !canKarigarConversion
                  ? "No permission for karigar conversion"
                  : undefined
              }
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Karigar;
